import {
  OperationVariables,
  QueryLazyOptions,
  useLazyQuery,
} from '@apollo/client';
import { Lead, UserProfile } from 'interfaces';
import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { isSalesAgent } from 'utils/roles';

import { getClients, getSalesAgentClients } from 'queries/clients';
import { getLeads } from 'queries/leads';

import { useSession } from 'providers/session';

interface AppointmentStatus {
  name: string;
  status: string;
}

interface BaseContextValue {
  clients: UserProfile[];
  leads: Lead[];
  leadsAggregate: any;
  refetchClients: any;
  loadLeads: (options?: QueryLazyOptions<OperationVariables>) => void;
  loadClients: (options?: QueryLazyOptions<OperationVariables>) => void;
}

const initialState = {};

const BaseContext = createContext(initialState as BaseContextValue);

function BP({ children }) {
  const { claims } = useSession();
  const salesAgent = isSalesAgent(claims);

  const [loadFullClients, fullClients] = useLazyQuery<{
    clients: UserProfile[];
  }>(getClients, {
    fetchPolicy: 'cache-and-network',
  });

  const [loadAgentClients, agentClients] = useLazyQuery<{
    clients: UserProfile[];
  }>(getSalesAgentClients, {
    fetchPolicy: 'cache-and-network',
  });

  const loadClients = salesAgent ? loadAgentClients : loadFullClients;
  const clients = salesAgent ? agentClients.data : fullClients.data;
  const refetchClients = salesAgent
    ? agentClients.refetch
    : fullClients.refetch;

  const [
    loadLeads,
    {
      called: leadsCalled,
      error: leadsError,
      data: leadsData,
      loading: leadsLoading,
      refetch: refetchLeads,
    },
  ] = useLazyQuery<{
    leads: Lead[];
    agg?: any;
  }>(getLeads, {
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!claims?.['x-hasura-role']) {
      return;
    }

    loadClients();
    const currentCount = localStorage.getItem('cc.activetabs')
      ? Number(localStorage.getItem('cc.activetabs'))
      : 0;
    localStorage.setItem('cc.activetabs', String(currentCount + 1));

    window.addEventListener('beforeunload', () => {
      const currentCount = localStorage.getItem('cc.activetabs')
        ? Number(localStorage.getItem('cc.activetabs'))
        : 1;
      localStorage.setItem('cc.activetabs', String(currentCount - 1));
    });
  }, [claims, loadClients]);

  const value: BaseContextValue = useMemo(
    () => ({
      clients: clients ? clients.clients : [],
      leads: leadsData ? leadsData.leads : [],
      leadsAggregate: leadsData ? leadsData.agg : {},
      loadClients,
      loadLeads,
      refetchClients,
    }),
    [clients, leadsData, loadClients, loadLeads, refetchClients],
  );

  return <BaseContext.Provider value={value}>{children}</BaseContext.Provider>;
}

const BaseProvider = BP;

export const useBase = () => useContext(BaseContext);

export default BaseProvider;
